import { useCallback } from 'react';

/**
 * Announce messages to screen readers via the persistent live regions
 * rendered in MainLayout.tsx.
 *
 * - 'polite' → #wheel-announcer (role="status", aria-live="polite")
 *   For non-urgent status: entry added/removed, spin start, shuffle.
 *
 * - 'assertive' → #wheel-announcer-urgent (role="alert", aria-live="assertive")
 *   For the winner reveal — interrupts current speech.
 *
 * The two-step textContent clear + rAF set ensures re-announcing the same
 * message still triggers the screen reader (e.g. same winner twice in a row).
 */
export function useAnnouncer() {
  const announce = useCallback(
    (message: string, priority: 'polite' | 'assertive' = 'polite') => {
      const id =
        priority === 'assertive'
          ? 'wheel-announcer-urgent'
          : 'wheel-announcer';
      const el = document.getElementById(id);
      if (!el) return;

      // Clear first so re-announcing the same message still fires
      el.textContent = '';
      requestAnimationFrame(() => {
        el.textContent = message;
      });
    },
    [],
  );

  return { announce };
}
